import React from 'react';

// ViewIssue: displays issue fields in organized read-only format
// Expects props: issue (object), formControls (array of control definitions), onClose (function)

// Field grouping configuration
const FIELD_GROUPS = {
  contact: ['name', 'email', 'phone'],
  location: ['address', 'latitude', 'longitude', 'city', 'state', 'zip'],
  issue: ['description', 'worktype', 'status'],
  other: []
};

const FIELD_ORDER = {
  worktype: 1,
  description: 2,
  address: 3,
  latitude: 4,
  longitude: 5,
  email: 1,
  phone: 2,
  name: 3,
  status: 4,
  category: 5
};

// Helper: sort fields by FIELD_ORDER
const sortFields = (fields) =>
  [...fields].sort((a, b) => (FIELD_ORDER[a] || 99) - (FIELD_ORDER[b] || 99));

// Check if field enabled
const isFieldEnabled = (formControls, fieldName) => {
  if (fieldName === 'image') return true;
  const ctrl = formControls.find(c => c.name === fieldName);
  return ctrl?.enabled ?? false;
};

// Get control definition
const getControl = (formControls, fieldName) => {
  if (fieldName === 'image') {
    return { name: 'image', label: 'Image', type: 'file', options: [] };
  }
  const control = formControls.find(c => c.name === fieldName);
  if (control) return control;
  // default fallback
  return { name: fieldName, label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1), type: 'text', options: [] };
};

// Render field value based on type
const renderFieldValue = (control, value) => {
  switch (control.type) {
    case 'select': {
      const opt = (control.options || []).find(o => o.value === value);
      return opt ? opt.label : value || '-';
    }
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'file':
      if (!value) return '-';
      try {
        console.log('Original image value:', value);
        
        // First try to decode HTML entities
        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        const decoded = textarea.value;
        console.log('Decoded value:', decoded);
        
        // Apply URI component decoding as well
        const decodedImage = decodeURIComponent(decoded);
        console.log('URI decoded value:', decodedImage);
        
        // Clean up the base64 string - remove any whitespace or line breaks
        const cleanBase64 = decodedImage.replace(/[\s\r\n]+/g, '');
        console.log('Cleaned base64:', cleanBase64);
        
        // Check if it's already a complete data URL
        if (cleanBase64.startsWith('data:image/')) {
          return (
            <img
              src={cleanBase64}
              alt={control.label}
              className="img-thumbnail"
              style={{ maxHeight: '200px', maxWidth: '100%' }}
              onError={(e) => {
                console.error('Image failed to load');
                e.target.style.display = 'none';
              }}
            />
          );
        }

        // Try to detect the image type from the base64 header
        let imageType = 'png'; // default
        if (cleanBase64.startsWith('/9j/')) {
          imageType = 'jpeg';
        } else if (cleanBase64.startsWith('iVBORw0KGgo')) {
          imageType = 'png';
        } else if (cleanBase64.startsWith('R0lGOD')) {
          imageType = 'gif';
        }
        
        // Construct the data URL with detected type
        const dataString = `data:image/${imageType};base64,${cleanBase64}`;
        console.log('Final data URL type:', imageType);
        
        return (
          <img
            src={dataString}
            alt={control.label}
            className="img-thumbnail"
            style={{ maxHeight: '200px', maxWidth: '100%' }}
            onError={(e) => {
              console.error('Image failed to load');
              e.target.style.display = 'none';
            }}
          />
        );
      } catch (error) {
        console.error('Error processing image:', error);
        return '-';
      }
    default:
      return value || '-';
  }
};

const ViewIssue = ({ formControls = [], issue = {}, onClose }) => {
  // Build groups and other fields
  const used = new Set(Object.values(FIELD_GROUPS).flat());
  const groups = { ...FIELD_GROUPS };
  
  // Add enabled form controls to groups
  formControls.forEach(ctrl => {
    if (ctrl.enabled && !used.has(ctrl.name)) {
      groups.other.push(ctrl.name);
    }
  });

  const decodeHtmlEntities = (str) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  };

  const renderImage = (image) => {
    if (!image) return null;

    try {
      const decodedHtml = decodeHtmlEntities(image);
      const decodedImage = decodeURIComponent(decodedHtml);
      const imageData = decodedImage.startsWith('data:') 
        ? decodedImage 
        : `data:image/jpeg;base64,${decodedImage}`;

      return (
        <div className="image-container" style={{ 
          maxHeight: '300px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <img 
            src={imageData} 
            alt="Issue" 
            style={{ 
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }} 
            onError={(e) => {
              console.error('Image failed to load');
              e.target.style.display = 'none';
            }}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering image:', error);
      return null;
    }
  };

  // Helper function to format field labels
  const formatFieldLabel = (field) => {
    return field
      .replace('custom_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!issue) return null;

  // Get all custom fields
  const customFields = Object.keys(issue).filter(key => 
    key.startsWith('custom_') && issue[key] !== null && issue[key] !== undefined
  );

  return (
    <div className="view-issue-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="view-issue-modal" style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer'
          }}
        >
          ×
        </button>

        <div className="issue-details">
          <h3 className="mb-4">Issue Details</h3>
          
          <div className="row">
            {/* Standard Fields */}
            <div className="col-md-6">
              <div className="detail-row mb-3">
                <label className="fw-bold">Submission ID:</label>
                <div>{issue.id}</div>
              </div>

              {/* Contact Information */}
              {groups.contact.map(field => (
                issue[field] && (
                  <div key={field} className="detail-row mb-3">
                    <label className="fw-bold">{formatFieldLabel(field)}:</label>
                    <div>{renderFieldValue(getControl(formControls, field), issue[field])}</div>
                  </div>
                )
              ))}
            </div>

            <div className="col-md-6">
              {/* Location Information */}
              {groups.location.map(field => (
                issue[field] && (
                  <div key={field} className="detail-row mb-3">
                    <label className="fw-bold">{formatFieldLabel(field)}:</label>
                    <div>{renderFieldValue(getControl(formControls, field), issue[field])}</div>
                  </div>
                )
              ))}

              {/* Issue Information */}
              {groups.issue.map(field => (
                issue[field] && (
                  <div key={field} className="detail-row mb-3">
                    <label className="fw-bold">{formatFieldLabel(field)}:</label>
                    <div>{renderFieldValue(getControl(formControls, field), issue[field])}</div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Other Fields */}
          {groups.other.length > 0 && (
            <div className="other-fields-section mt-4">
              <h4 className="mb-3">Other Information</h4>
              <div className="row">
                {groups.other.map(field => (
                  issue[field] && (
                    <div key={field} className="col-md-6 mb-3">
                      <div className="detail-row">
                        <label className="fw-bold">{formatFieldLabel(field)}:</label>
                        <div>{renderFieldValue(getControl(formControls, field), issue[field])}</div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Custom Fields Section */}
          {customFields.length > 0 && (
            <div className="custom-fields-section mt-4">
              <h4 className="mb-3">Additional Information</h4>
              <div className="row">
                {customFields.map(field => (
                  <div key={field} className="col-md-6 mb-3">
                    <div className="detail-row">
                      <label className="fw-bold">{formatFieldLabel(field)}:</label>
                      <div>{renderFieldValue(getControl(formControls, field), issue[field])}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence Section */}
          {issue.image && (
            <div className="evidence-section mt-4">
              <h4 className="mb-3">Evidence</h4>
              {renderImage(issue.image)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewIssue; 